import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { inject, isDevMode, NgModule, provideAppInitializer, Provider } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TuiLanguageName, tuiLanguageSwitcher } from '@taiga-ui/i18n';
import { provideEchartsCore } from 'ngx-echarts';

import { NightThemeComponent } from '@common/components/night-theme/night-theme.component';
import { OBJECT_CARD_ADAPTER_TOKEN } from '@common/components/object-card-adapter';
import { SidePageModule } from '@common/components/sidepage/sidepage.module';
import { errorInterceptor } from '@common/interceptors/error.interceptor';
import { loaderInterceptor } from '@common/interceptors/loader.interceptor';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { mainReducers, rootEffects } from '@store/types/store-types';

import { apiInterceptor } from './api.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ObjectCardAdapterService } from './portal/components/object-card/adapters/object-card.adapter';
import { TaigaModule } from './taiga-ui.module';

function getProviders(): Provider[] {
  const providers: Provider[] = [
    { provide: OBJECT_CARD_ADAPTER_TOKEN, useExisting: ObjectCardAdapterService },
    provideEchartsCore({ echarts: () => import('echarts') }),
    tuiLanguageSwitcher(async (language: TuiLanguageName): Promise<unknown> => {
      switch (language) {
        case `ru`:
        case `russian`:
          return import(`@taiga-ui/i18n/languages/russian`);
        case `fr`:
        case `french`:
          return import(`@taiga-ui/i18n/languages/french`);
        default:
          return import(`@taiga-ui/i18n/languages/english`);
      }
    }),
  ];
  return providers;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    StoreModule.forRoot(mainReducers),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode(), connectInZone: true }),
    EffectsModule.forRoot(rootEffects),
    AppRoutingModule,
    BrowserAnimationsModule,
    TaigaModule,
    SidePageModule,
    NightThemeComponent,
  ],
  providers: [
    ...getProviders(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './common/assets/i18n/', suffix: '.json' }),
    }),
    provideHttpClient(
      withXhr(),
      withInterceptors([apiInterceptor, errorInterceptor, loaderInterceptor]),
    ),
    provideAppInitializer(() => inject(FeatureFlagsService).init()),
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}
